import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import api from 'api';
import { MyPageTable } from 'components/common/myPageTable/MyPageTable';
import { Button } from 'components/common/Button/Button';
import { ContactPersonModal } from 'components/SupplierCard/ContactPersonModal/ContactPersonModal';
import { selectSelectedSupplierId } from 'store/suppliersList/Selector';
import { startLoading, finishLoading } from 'hooks/useProgress';
import { SupplierId } from 'store/suppliersList/types';
import { AppState } from 'store/RootReducer';
import { ContactPerson, ContactPersonList } from 'components/SupplierCard/Tabs/ContactPersonsTab/types';
import { showSnackbar } from 'hooks/useSnackbar';
import cn from './ContactPersonsTab.module.scss';

type MapStateToProps = {
  selectedSupplierId: SupplierId;
};

type ContactPersonsTabProps = MapStateToProps;

const ContactPersonsTabComponent = React.memo(({ selectedSupplierId }: ContactPersonsTabProps) => {
  const [data, setData] = useState<ContactPersonList>([]);
  const [opened, setOpened] = useState(false);
  const [editedRow, setEditedRow] = useState<ContactPerson | null>(null);

  const requestData = useCallback(async () => {
    startLoading();
    try {
      const contactPersons = await api.getContactPersons(selectedSupplierId);
      setData(contactPersons || []);
    } catch (err) {
    }
    finishLoading();
  }, [selectedSupplierId]);

  function handleOpenModal(): void {
    setOpened(true);
  }

  function handleCloseModal(): void {
    setOpened(false);
    setEditedRow(null);
  }

  function handleEditRowClick(rowId: number): void {
    setEditedRow(data.find((row) => row.id === rowId) || null);
    setOpened(true);
  }

  function validateSosMain(contacts: ContactPersonList): void {
    const hasSos = contacts.some(c => c.sos);
    const hasMain = contacts.some(c => c.main);
    if (!hasSos || !hasMain) {
      showSnackbar({
        message: `Missing required contacts: ${!hasSos ? 'SOS ' : ''}${!hasMain ? 'Main' : ''}`,
        type: 'warning',
      });
    }
  }

  async function handleFormSubmit(contactPerson: ContactPerson): Promise<void> {
    if (!contactPerson.email?.trim()) {
      showSnackbar({
        message: 'Email is required',
        type: 'error',
      });
      return;
    }
    if (editedRow && editedRow.id) {
      await api.editContactPerson(selectedSupplierId, editedRow.id, {
        ...contactPerson,
      });
      showSnackbar({
        message: 'Contact person was updated.',
        type: 'success',
      });
      setEditedRow(null);
      const contacts = await api.getContactPersons(selectedSupplierId);
      setData(contacts || []);
      validateSosMain(contacts || []);
    } else {
      await api.addContactPerson(selectedSupplierId, contactPerson);
      showSnackbar({
        message: 'Contact person was successfully created.',
        type: 'success',
      });
      const contacts = await api.getContactPersons(selectedSupplierId);
      setData(contacts || []);
      validateSosMain(contacts || []);
    }
    setOpened(false);
  }

  useEffect(() => {
    if (selectedSupplierId) {
      requestData();
    }
  }, [selectedSupplierId, requestData]);

  return (
    <div className={cn.tabContainer}>
      <Button
        onClick={handleOpenModal}
        className={cn.button}
      >
        Add New Contact
      </Button>
      <MyPageTable
        tableData={data}
        padding="small"
        filter
        edit
        onRowEdit={handleEditRowClick}
        columns={[
          { name: 'name', title: 'Name' },
          { name: 'email', title: 'Email' },
          { name: 'telephone', title: 'Telephone' },
          { name: 'title', title: 'Title' },
          { name: 'sos', title: 'SOS' },
          { name: 'main', title: 'Main' },
        ]}
        formatBoolean={['sos', 'main']}
        booleanIcons={[
          { fieldName: 'sos', yes: { icon: CheckCircle, color: '#087f23' }, no: { icon: RadioButtonUnchecked, color: '#bdbdbd' } },
          { fieldName: 'main', yes: { icon: CheckCircle, color: '#087f23' }, no: { icon: RadioButtonUnchecked, color: '#bdbdbd' } },
        ]}
        defaultSorting={[
          { columnName: 'name', direction: 'asc' },
        ]}
      />
      {opened && (
        <ContactPersonModal
          isOpen={opened}
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
          editedRow={editedRow}
        />
      )}
    </div>
  );
});

const mapStateToProps = (state: AppState): MapStateToProps => ({
  selectedSupplierId: selectSelectedSupplierId(state),
});

export const ContactPersonsTab = connect(mapStateToProps)(ContactPersonsTabComponent);
